import jsPDF from 'jspdf';
import { formatDateOnlyFr } from './dateFormat';

const CATEGORY_LABELS = {
  general: 'Général',
  sav: 'SAV',
  rdv: 'Rendez-vous',
  client: 'Client',
  interne: 'Interne',
};

const STATUS_LABELS = {
  pas_encore: 'Pas encore',
  en_cours: 'En cours',
  traite: 'Traité',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('fr-FR', {
    timeZone: 'Africa/Casablanca',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const roleLabel = (role) => {
  if (role === 'admin') return 'Admin';
  if (role === 'manager') return 'Manager';
  if (role === 'employee') return 'Employe';
  return role || '';
};

/** Ticket thermique 80 mm — meme format que le ticket commande */
export const generateNoteTicketPDF = (note) => {
  if (!note) return;

  const pageWidth = 80;
  const margin = 5;
  const contentWidth = pageWidth - margin * 2;
  const primaryColor = [0, 51, 102];
  const textColor = [51, 51, 51];

  const bodyText = String(note.body || '').trim();
  const titleText = String(note.title || 'Sans titre').trim();

  const probe = new jsPDF({ unit: 'mm', format: [pageWidth, 200] });
  probe.setFontSize(9);
  const bodyLines = bodyText
    ? probe.splitTextToSize(bodyText, contentWidth)
    : ['(vide)'];
  probe.setFontSize(11);
  const titleLines = probe.splitTextToSize(titleText, contentWidth);

  let estimatedHeight = 10;
  estimatedHeight += 8 + 4 + 8 + 5;
  estimatedHeight += titleLines.length * 5 + 4;
  estimatedHeight += bodyLines.length * 4 + 6;
  if (note.client_name || note.client_phone) estimatedHeight += 14;
  if (note.sales_order_id) estimatedHeight += 6;
  estimatedHeight += 18;

  const pageHeight = Math.max(120, Math.min(400, estimatedHeight));
  const doc = new jsPDF({ unit: 'mm', format: [pageWidth, pageHeight] });

  let y = 10;

  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('EPOPTIQUE', pageWidth / 2, y, { align: 'center' });
  y += 8;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textColor);
  doc.text('0522-22-55-25 / 07-64-200-156', pageWidth / 2, y, { align: 'center' });
  y += 4;

  doc.setFontSize(7);
  doc.text('8 RUE ABOU ALWAQT KHALAF, BOURGOGNE CASABLANCA', pageWidth / 2, y, { align: 'center' });
  y += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(`Note n°${note.id}`, margin, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.text(formatDateTime(note.created_at), pageWidth - margin, y - 5, { align: 'right' });

  const categoryLabel = CATEGORY_LABELS[note.category] || note.category || 'General';
  doc.text(`Catégorie : ${categoryLabel}`, margin, y);
  y += 5;

  const statusLabel = STATUS_LABELS[note.status] || STATUS_LABELS.pas_encore;
  doc.text(`Statut : ${statusLabel}`, margin, y);
  y += 5;

  if (note.store_name) {
    doc.text(`Magasin : ${note.store_name}`, margin, y);
    y += 5;
  }

  y += 2;
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageWidth - margin, y);
  y += 6;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  titleLines.forEach((line) => {
    doc.text(line, margin, y);
    y += 5;
  });
  y += 2;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  bodyLines.forEach((line) => {
    doc.text(line, margin, y);
    y += 4;
  });
  y += 4;

  if (note.client_name || note.client_phone) {
    doc.setDrawColor(180, 180, 180);
    doc.line(margin, y, pageWidth - margin, y);
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Client', margin, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    if (note.client_name) {
      doc.text(note.client_name, margin, y);
      y += 4;
    }
    if (note.client_phone) {
      doc.text(note.client_phone, margin, y);
      y += 4;
    }
  }

  if (note.sales_order_id) {
    y += 2;
    doc.setFont('helvetica', 'bold');
    doc.text(`Commande #${note.sales_order_id}`, margin, y);
    y += 4;
    if (note.order_client_name) {
      doc.setFont('helvetica', 'normal');
      doc.text(note.order_client_name, margin, y);
      y += 4;
    }
  }

  y += 4;
  doc.setDrawColor(180, 180, 180);
  doc.line(margin, y, pageWidth - margin, y);
  y += 5;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Par : ${note.author_name || '-'} (${roleLabel(note.author_role)})`, margin, y);
  y += 4;
  doc.text(`Créée : ${formatDateTime(note.created_at)}`, margin, y);
  if (note.updated_at && note.updated_at !== note.created_at) {
    y += 4;
    doc.text(`Modifiée : ${formatDateTime(note.updated_at)}`, margin, y);
  }

  if (note.is_pinned) {
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.text('Note épinglée', margin, y);
  }

  y += 8;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text(`Imprimé le ${formatDateOnlyFr(new Date())}`, pageWidth / 2, y, { align: 'center' });

  doc.save(`Note_${note.id}.pdf`);
};
